import { useEffect, useMemo, useState } from "react";
import { addDays, format } from "date-fns";
import { ChevronLeft, ChevronRight, Moon, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { fmtTime, isOvernight, ymd } from "@/lib/dates";
import { periodFor } from "@/lib/rotaPeriod";
import { useAuth } from "@/context/AuthContext";
import { CalendarSync } from "@/pages/staff/CalendarSync";

const Row = ({ s }) => (
  <tr className="border-b border-slate-100" data-testid={`month-shift-${s.id}`}>
    <td className="whitespace-nowrap py-2.5 pr-3 font-semibold">{format(new Date(s.start), "EEE d MMM")}</td>
    <td className="whitespace-nowrap py-2.5 pr-3 font-mono text-xs sm:text-sm">{fmtTime(s.start)}–{fmtTime(s.end)}{isOvernight(s) && <span className="ml-1 rounded bg-slate-900 px-1 text-[10px] font-bold text-white">+1</span>}</td>
    <td className="py-2.5 pr-3">
      <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: s.shift_type?.colour || "#94a3b8" }} />
      {s.shift_type?.name || "Shift"}{s.shift_type?.is_sleepover && <Moon className="ml-1 inline h-3.5 w-3.5 text-purple-600" />}
    </td>
    <td className="hidden py-2.5 pr-3 text-slate-600 sm:table-cell print:table-cell">{s.location_name || "–"}</td>
    <td className="py-2.5 text-right font-mono">{s.paid_hours.toFixed(1)}h</td>
  </tr>
);

const Totals = ({ shifts }) => {
  const hours = shifts.reduce((a, s) => a + s.paid_hours, 0);
  const sleeps = shifts.filter((s) => s.shift_type?.is_sleepover).length;
  const pl = (n, w) => `${w}${n === 1 ? "" : "s"}`;
  return (
    <div className="flex flex-wrap gap-6 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm" data-testid="month-totals">
      <span><b className="font-display text-lg">{shifts.length}</b> {pl(shifts.length, "shift")}</span>
      <span><b className="font-display text-lg">{hours.toFixed(1)}</b> paid hours</span>
      {sleeps > 0 && <span><b className="font-display text-lg">{sleeps}</b> {pl(sleeps, "sleepover")}</span>}
    </div>
  );
};

export default function MyMonth() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [anchor, setAnchor] = useState(() => new Date());
  const [shifts, setShifts] = useState(null);
  useEffect(() => {
    api.get("/me/rota-view").then((r) => setCfg(r.data)).catch(() => setCfg({ mode: "month" }));
  }, []);
  const period = useMemo(() => (cfg ? periodFor(anchor, cfg.mode === "custom_month" ? { mode: "custom_month", monthDay: cfg.monthDay } : { mode: "month" }) : null), [cfg, anchor]);
  useEffect(() => {
    if (!period) {
      return;
    }
    setShifts(null);
    api.get("/me/shifts", { params: { start: ymd(period.start), end: ymd(period.end) } }).then((r) => setShifts(r.data));
  }, [period]);

  const label = period ? `${format(period.start, "d MMM")} – ${format(period.end, "d MMM yyyy")}` : "";
  return (
    <div className="my-month-page mx-auto max-w-3xl space-y-5">
      <div className="print-only">
        <p className="font-display text-xl font-bold">{user?.name} · {user?.business?.name}</p>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="no-print text-sm text-slate-500">{user?.business?.name}</p>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="page-title">My month</h1>
          <p className="text-sm text-slate-600" data-testid="month-period-label">{label}</p>
        </div>
        <div className="no-print flex flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" disabled={!period} onClick={() => setAnchor(addDays(period.start, -1))} data-testid="month-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setAnchor(new Date())} data-testid="month-today-btn">This month</Button>
          <Button variant="outline" size="icon" disabled={!period} onClick={() => setAnchor(addDays(period.end, 1))} data-testid="month-next-btn"><ChevronRight className="h-4 w-4" /></Button>
          <CalendarSync />
          <Button className="rounded-full" disabled={!shifts?.length} onClick={() => window.print()} data-testid="month-print-btn"><Printer className="mr-1 h-4 w-4" />Print or save PDF</Button>
        </div>
      </div>
      {shifts && <Totals shifts={shifts} />}
      {shifts && shifts.length === 0 && <Empty testId="month-empty">No published shifts for these dates.</Empty>}
      {shifts?.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white px-3 py-2 sm:px-5 print-month">
          <table className="w-full text-sm" data-testid="month-shift-table">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Time</th><th className="py-2 pr-3">Shift</th><th className="hidden py-2 pr-3 sm:table-cell print:table-cell">Location</th><th className="py-2 text-right">Hours</th>
            </tr></thead>
            <tbody>{shifts.map((s) => <Row key={s.id} s={s} />)}</tbody>
          </table>
        </div>
      )}
      <p className="print-only text-[9px] text-slate-500">Printed {format(new Date(), "d MMM yyyy HH:mm")} from StaffRotas.com. Rotas can change, so check the app for the latest version.</p>
    </div>
  );
}
